import { lazy, ComponentType } from 'react';

const RELOAD_KEY = 'lovable_chunk_reload_at';
const RETRY_DELAY_MS = 800;

/**
 * lazy() wrapper that recovers from stale dynamic-import chunks (common
 * after a new deploy when the previous chunk hash no longer exists on the
 * CDN). Strategy:
 *   1. On first failure → wait briefly and retry the import (handles
 *      transient network/CDN hiccups without a full page reload).
 *   2. If the retry also fails AND we have NOT reloaded in this session →
 *      perform a one-time hard reload to fetch the new chunk manifest.
 *   3. Otherwise propagate the error so ErrorBoundary handles it.
 *
 * The previous implementation reloaded on EVERY navigation that surfaced
 * a dynamic-import error, which made it feel like the app "refreshed
 * every time you switched pages". Using sessionStorage as a hard one-shot
 * gate fixes that.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err1: any) {
      const msg1 = String(err1?.message || err1);
      const isChunkError =
        msg1.includes('dynamically imported module') ||
        msg1.includes('Failed to fetch dynamically imported module') ||
        msg1.includes('error loading dynamically imported') ||
        msg1.includes('Importing a module script failed') ||
        msg1.includes('ChunkLoadError');

      if (!isChunkError) throw err1;

      // Retry once after a short delay — covers transient network blips
      try {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        return await factory();
      } catch (err2: any) {
        if (typeof window === 'undefined') throw err2;

        // Only ever reload ONCE per browser session to avoid loops and to
        // avoid feeling like the page refreshes on every navigation.
        const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
          return new Promise(() => {}) as any;
        }
        throw err2;
      }
    }
  });
}